import { ProgressSpinner } from "primereact/progressspinner";
import { Badge } from "component/Badge/Badge";
import {
  REQUIRED_COMPLETED_STEPS,
  WORKFLOW_STATUS_UI,
  type WorkflowStatus,
} from "./useAudioWorkflowStatus";

const GRID_COLUMNS = "2.4fr 1fr 1.2fr 1.4fr";

const STATUS_BADGE: Record<
  Exclude<WorkflowStatus, "idle">,
  "Info" | "Success" | "Error" | "Neutral"
> = {
  pending: "Neutral",
  running: "Info",
  succeeded: "Success",
  failed: "Error",
};

/** One row of the audio workflows landing list, with its status already derived. */
export interface WorkflowListRow {
  id: string;
  fileUrl: string;
  fileName: string;
  status: Exclude<WorkflowStatus, "idle">;
  completedCount: number;
  updatedAt?: string;
}

/** Compact relative timestamp ("5m ago") for the Updated column. */
const timeAgo = (iso?: string): string => {
  if (!iso) return "—";
  const timestamp = Date.parse(iso);
  if (Number.isNaN(timestamp)) return iso;
  const seconds = Math.max(0, Math.floor((Date.now() - timestamp) / 1000));
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(timestamp).toLocaleDateString();
};

interface WorkflowsListTableProps {
  rows: WorkflowListRow[];
  loading: boolean;
  onRowClick: (row: WorkflowListRow) => void;
}

const WorkflowsListTable = ({ rows, loading, onRowClick }: WorkflowsListTableProps) => {
  if (loading && rows.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center">
        <ProgressSpinner />
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-16">
        <i className="pi pi-inbox text-3xl" style={{ color: "var(--color-icon-disabled)" }} />
        <span className="text-sm" style={{ color: "var(--color-text-secondary)" }}>
          No workflows yet
        </span>
        <span className="text-sm" style={{ color: "var(--color-text-tertiary)" }}>
          Try adjusting your filter, or upload an audio file to start a workflow.
        </span>
      </div>
    );
  }

  return (
    <div
      style={{
        border: "1px solid var(--color-border-quaternary)",
        borderRadius: 8,
        overflow: "hidden",
      }}
    >
      <div
        className="grid px-4 py-2 text-[10px] font-semibold tracking-wider uppercase"
        style={{
          gridTemplateColumns: GRID_COLUMNS,
          background: "var(--color-surface-secondary)",
          borderBottom: "1px solid var(--color-border-quaternary)",
          color: "var(--color-text-tertiary)",
        }}
      >
        <span>File</span>
        <span>Steps</span>
        <span>Status</span>
        <span>Updated</span>
      </div>

      {rows.map((row, i) => {
        const ui = WORKFLOW_STATUS_UI[row.status];
        return (
          <div
            key={row.id}
            className="grid cursor-pointer items-center px-4 py-2.5 text-xs transition-colors"
            style={{
              gridTemplateColumns: GRID_COLUMNS,
              borderTop: i > 0 ? "1px solid var(--color-border-weak)" : "none",
              color: "var(--color-text-primary)",
              background: "var(--color-surface-primary)",
            }}
            onClick={() => onRowClick(row)}
            onMouseEnter={(e) => (e.currentTarget.style.background = "var(--color-surface-secondary)")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "var(--color-surface-primary)")}
          >
            <span className="truncate pr-2 font-medium" title={row.fileUrl}>
              {row.fileName}
            </span>
            <span style={{ color: "var(--color-text-secondary)" }}>
              {row.completedCount}/{REQUIRED_COMPLETED_STEPS}
            </span>
            <span>
              <Badge
                label={ui.label}
                variant="secondary"
                state={STATUS_BADGE[row.status]}
                size="Small"
                statusDot={row.status === "running"}
              />
            </span>
            <span style={{ color: "var(--color-text-secondary)" }}>{timeAgo(row.updatedAt)}</span>
          </div>
        );
      })}
    </div>
  );
};

export default WorkflowsListTable;
